import {React, useEffect, useState, useRef} from "react";
import { useDispatch } from "react-redux";
import classes from './Analytics.module.css'
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { Oval } from 'react-loading-icons';
import { Dropdown } from "primereact/dropdown";
import { getSiteAnalytics } from "../../../Api Methods/Api";
import { set } from "date-fns";

const AnalyticsPage = ({ siteUUID, apiData, iglooLocks, isCollapsed, OutPutsPinNames }) => {       // gna pass things in 

    const [activeTab, setActiveTab] = useState(0);
    const [selectedTime, setSelectedTime] = useState("Week");
    const [scheduleFailsData, setScheduleFailsData] = useState([]);
    const [securityData, setSecurityData] = useState([]);
    const [unlockData, setUnlockData] = useState([]);
    const [topUnlockData, setTopUnlockData] = useState([]);
    const [selectedLock, setSelectedLock] = useState(null);

    const [isLoadingSchedules, setIsLoadingSchedules] = useState(true);
    const [isLoadingSecurity, setIsLoadingSecurity] = useState(true);
    const [isLoadingLocks, setIsLoadingLocks] = useState(true);
    const [isCheckingTabs, setIsCheckingTabs] = useState(true);
    const [hasSchedules, setHasSchedules] = useState(true);

    const scheduleRequestId = useRef(0);
    const securityRequestId = useRef(0);
    const lockRequestId = useRef(0);

    const tabs = [
        hasSchedules && "Schedules",
        "Security",
        "Padlocks"
    ].filter(Boolean)
    const times = ["Week", "Month", "Year"]

    const dispatch = useDispatch();

    const lockOptions = iglooLocks?.filter(lock => lock !== null && lock !== undefined).map(lock => {
        const pin = OutPutsPinNames?.filter(Pin => Pin.Pin == lock.outputPin);
        const pinName = pin?.[0]?.pinName || "Unknown";
        return { label: `${pinName} - ${lock.lockID}`, value: lock.lockID };
    }) || [];

    const getScheduleData = async (rangeType) => {
        const currentRequestId = ++scheduleRequestId.current;
        setIsLoadingSchedules(true);
        setScheduleFailsData([]);
        const body = {
            siteUUID: siteUUID,
            rangeType: rangeType.toLowerCase(),
            analyticsType: "schedules"
        }
        const res = await getSiteAnalytics(apiData, body, dispatch)
        if (currentRequestId !== scheduleRequestId.current) return;
        setScheduleFailsData(res.data[0])
        setIsLoadingSchedules(false);
    }

    const getScheduleFailStats = (scheduleFailsData) => {
        if (!scheduleFailsData || scheduleFailsData.length === 0) return { totalFails: 0, successRate: 0, totalSuccess: 0 };

        const totalFails = scheduleFailsData.reduce((total, item) => total + item.fail, 0);
        const totalSuccess = scheduleFailsData.reduce((total, item) => total + item.success, 0);
        const total = totalFails + totalSuccess;
        const successRate = total === 0 ? 0 : (totalSuccess / total * 100).toFixed(1);
        return { totalFails, successRate, totalSuccess };
    }

    const getSecurityData = async (rangeType) => {
        const currentRequestId = ++securityRequestId.current;
        setSecurityData([]);
        setIsLoadingSecurity(true);
        const body = {
            siteUUID: siteUUID,
            rangeType: rangeType.toLowerCase(),
            analyticsType: "gateways"
        }
        const res = await getSiteAnalytics(apiData, body, dispatch)
        if (currentRequestId !== securityRequestId.current) return;
        setSecurityData(res.data[0])
        setIsLoadingSecurity(false);
    }

    const getSecurityStats = (securityData) => {
        if (!securityData || securityData.length === 0) return { totalInvalidPins: 0, totalTampers: 0 };

        const totalInvalidPins = securityData.reduce((total, item) => total + item.invalid_pin_count, 0);
        const totalTampers = securityData.reduce((total, item) => total + item.tamper_count, 0);
        return { totalInvalidPins, totalTampers };
    }

    const getUnlockData = async (rangeType) => {
        const currentRequestId = ++lockRequestId.current;
        setUnlockData([]);
        setIsLoadingLocks(true);
        const body = {
            siteUUID: siteUUID,
            lockID: selectedLock,
            rangeType: rangeType.toLowerCase(),
            analyticsType: "locks"
        }
        const res = await getSiteAnalytics(apiData, body, dispatch)
        if (currentRequestId !== lockRequestId.current) return;
        setUnlockData(res.data[0].unlocks)
        setTopUnlockData(res.data[0].mostActiveUsers)
        setIsLoadingLocks(false);
    }

    const getLockStats = (unlockData) => {
        if (!unlockData || unlockData.length === 0) return { totalUnlocks: 0 }
        const totalUnlocks = unlockData.reduce((total, item) => total + item.count, 0);
        return { totalUnlocks }
    }

    const barChartData = {
        labels: scheduleFailsData.map(item => item.date),
        datasets: [
            {
                label: 'Failed Schedules',
                data: scheduleFailsData.map(item => item.fail),
                backgroundColor: 'rgba(255, 99, 132, 0.5)',
                borderColor: 'rgba(255, 99, 132, 1)',
                borderWidth: 1,
                borderRadius: 8,
            }
        ]
    }

    const doughnutData = {
        labels: ['Success', 'Failed'],
        datasets: [
            {
                label: 'Schedule Success Rate',
                data: [scheduleFailsData.reduce((total, item) => total + item.success, 0), scheduleFailsData.reduce((total, item) => total + item.fail, 0)],
                backgroundColor: [
                    'rgba(86, 173, 156, 0.5)',
                    'rgba(255, 99, 132, 0.5)'
                ],
                borderColor: [
                    '#56AD9C',
                    'rgba(255, 99, 132, 1)'
                ],
                borderWidth: 1,
            }
        ]
    };

    const lineData1 = {
        labels: securityData.map(item => item.date),
        datasets: [
            {
                label: "Invalid PIN Attempts",
                
                data: securityData.map(item => item.invalid_pin_count),
                fill: true,
                backgroundColor: 'rgba(221, 81, 68, 0.04)',
                borderColor: 'rgba(221, 80, 68, 1)',
                tension: 0.5
            },
            {
                label: "Gateway Tampers",
                data: securityData.map(item => item.tamper_count),
                fill: true,
                backgroundColor: 'rgba(225, 239, 16, 0.09)',
                borderColor: 'rgb(191, 220, 29)',
                tension: 0.5
            }
        ]
    };

    const fallbackLineData1 = {
        labels: securityData.map(item => item.date),
        datasets: [
            {
                label: "Invalid PIN Attempts",
                
                data: securityData.map(item => item.invalid_pin_count),
                fill: true,
                backgroundColor: 'rgba(255, 99, 132, 0.5)',
                borderColor: 'rgba(221, 80, 68, 1)',
                borderRadius: 8,
                tension: 0.5
            },
            {
                label: "Gateway Tampers",
                data: securityData.map(item => item.tamper_count),
                fill: true,
                backgroundColor: 'rgba(191, 220, 29, 0.5)',
                borderColor: 'rgb(191, 220, 29)',
                borderRadius: 8,
                tension: 0.5
            }
        ]
    };

    const lineData2 = {
        labels: unlockData.map(item => item.date),
        datasets: [
            {
                label: `Lock - ${selectedLock}`,
                data: unlockData.map(item => item.count),
                fill: true,
                backgroundColor: 'rgba(128, 119, 221, 0.04)',
                borderColor: 'rgba(128, 119, 221, 1)',
                tension: 0.5,
                pointRadius: 6,
                pointHoverRadius: 8
            }
        ]
    };

    const fallbackLineData2 = {
        labels: unlockData.map(item => item.date),
        datasets: [
            {
                label: `Lock - ${selectedLock}`,
                data: unlockData.map(item => item.count),
                backgroundColor: 'rgba(0, 200, 255, 0.5)',
                borderColor: 'rgb(0, 200, 255)',
                borderWidth: 1,
                borderRadius: 8,
            }
        ]
    };

    const barData2 = {
        labels: topUnlockData.map(item => item.user),
        datasets: [
            {
                label: "Unlocks",
                data: topUnlockData.map(item => item.count),
                backgroundColor: [
                    'rgba(86, 173, 156, 0.5)',
                    'rgba(0, 200, 255, 0.5)',
                    'rgba(191, 0, 255, 0.5)',
                    'rgba(255, 89, 0, 0.5)',
                    'rgba(6, 135, 28, 0.5)'
                ],
                borderColor: [ 
                    'rgb(86, 173, 155)',
                    'rgb(0, 200, 255)',
                    'rgb(191, 0, 255)',
                    'rgb(255, 89, 0)',
                    'rgb(6, 135, 28)'
                ],
                borderWidth: 1,
                borderRadius: 8,
            }
        ]
    };


    // schedule check
    useEffect(() => {
        const checkSchedules = async () => {
            const body = {
                siteUUID: siteUUID,
                rangeType: "year",
                analyticsType: "schedules"
            }
            const res = await getSiteAnalytics(apiData, body, dispatch);
            const data = res.data[0];
            const totalFails = data.reduce((total, item) => total + item.fail, 0);
            const totalSuccess = data.reduce((total, item) => total + item.success, 0);
            setHasSchedules(totalFails > 0 || totalSuccess > 0);
            setIsCheckingTabs(false);
        };
        if (siteUUID && apiData) checkSchedules();
    }, [siteUUID])

    useEffect(() => {
        if (siteUUID && apiData && !isCheckingTabs && tabs[activeTab] === "Schedules") {
            getScheduleData(selectedTime)
        }
    }, [selectedTime, siteUUID, activeTab, isCheckingTabs])

    useEffect(() => {
        if (siteUUID && apiData && !isCheckingTabs && tabs[activeTab] === "Security") {
            getSecurityData(selectedTime)
        }
    }, [selectedTime, siteUUID, activeTab, isCheckingTabs])

    useEffect(() => {
        if (siteUUID && apiData && !isCheckingTabs && selectedLock && tabs[activeTab] === "Padlocks") {
            getUnlockData(selectedTime)
        }
    }, [selectedTime, selectedLock, activeTab, isCheckingTabs])

    if (isCheckingTabs) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
                <Oval stroke="var(--Primary-Color)" height={200} width={100} speed={1.5} />
            </div>
        );
    }

    return (
        <>

        <div className={classes.tabs}>
            {tabs.map((tab, i) => (
                <button key={tab} 
                    className={`${classes.tab} ${activeTab === i ? classes.active : ""}`}
                    onClick={() => setActiveTab(i)}>
                    {tab}
                </button>
            ))}
        </div>
        
        
        {tabs[activeTab] === "Schedules" && (
        <div className={isCollapsed ? classes.schedulesContainer : classes.schedulesContainerCol}>
            <div className={classes.scheduleFails}>
                <div className={`${classes.statsHeader} ${isCollapsed ? classes.statsHeader : classes.statsHeaderCol}`}>
                    <div className={classes.title}>
                        <h2>Schedule fails</h2>
                        <p>Failed schedules over time</p>
                    </div>
                    <div className={classes.timeButtons}>
                        {times.map((time) => (
                            <button
                                key={time}
                                className={selectedTime === time ? classes.timeButtonActive : classes.timeButton}
                                onClick={() => setSelectedTime(time)}
                            >
                                {time}
                            </button>
                        ))}
                    </div>
                </div>
                {isLoadingSchedules ? (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "437px" }}> 
                        <Oval 
                        stroke="var(--Primary-Color)" 
                        height={80} width={40} speed={1.5}
                        />
                     </div>
                ) : 
                getScheduleFailStats(scheduleFailsData).totalFails === 0 ? (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "437px" }}>
                        <p style={{ fontSize: "14px", fontWeight: "400" }}>
                            No schedule fails for the selected time period.
                        </p>
                    </div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "32px", marginLeft: "8px" }}>
                        <div style={{ height: "350px", width: isCollapsed ? "600px" : "min(100%, 600px)" }}>
                            <Bar
                                data={barChartData}
                                options={{
                                        scales: {
                                            y: {
                                                ticks: {
                                                    stepSize: 1,
                                                    precision: 0
                                                }
                                            }
                                        }
                                    }}
                            />
                        </div>

                        <p style={{ fontSize: "14px", fontWeight: "400", opacity: "0.7", marginTop: "24px" }}>
                            Total fails: {getScheduleFailStats(scheduleFailsData).totalFails}
                        </p>
                    </div>
                )}
            </div>


            <div style ={{ display: "flex", flexDirection: "column", alignItems: "center", width: isCollapsed ? "" : "min(85%, 650px)"}}>
                <div className={`${classes.title} ${isCollapsed ? classes.title : classes.titleCol}`} style={{ alignSelf: isCollapsed ? "center" : "flex-start", marginLeft: "22px" }}>
                    <h2>Schedule success rate</h2>
                    <p>Pass/fail breakdown for all schedules</p>
                </div>
                {isLoadingSchedules ? (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "437px" }}> 
                        <Oval 
                        stroke="var(--Primary-Color)" 
                        height={80} width={40} speed={1.5}
                        />
                     </div>
                ) : 
                getScheduleFailStats(scheduleFailsData).successRate === 0 ? (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "437px" }}>
                        <p style={{ fontSize: "14px", fontWeight: "400" }}>
                            No data available.
                        </p>
                    </div>
                ) : (
                    <>
                        <div style={{ height: "350px", marginTop: "32px"}}>
                            <Doughnut data={doughnutData} />
                        </div>

                        <p style={{ fontSize: "14px", fontWeight: "400", opacity: "0.7", marginTop: "38px" }}>
                            Success rate: {getScheduleFailStats(scheduleFailsData).successRate}%
                        </p>
                    </>
                )}
            </div>
        </div>
        )}

        {tabs[activeTab] === "Security" && (
            <div>
                <div className={`${classes.securityStatsHeader} ${isCollapsed ? classes.securityStatsHeader : classes.securityStatsHeaderCol}`}>
                    <div className={classes.title}>
                        <h2>Security alerts</h2>
                        <p>Invalid PIN attempts and gateway tamper events</p>
                    </div>
                    <div className={classes.timeButtons}>
                        {times.map((time, i) => (
                            <button 
                                key={time} 
                                className={`classes.timeButton ${selectedTime === time ? classes.timeButtonActive : classes.timeButton}`}
                                onClick={() => setSelectedTime(time)}
                            >
                                {time}
                            </button>
                        ))}
                    </div>
                </div>

                {isLoadingSecurity ? (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
                        <Oval stroke="var(--Primary-Color)" height={80} width={40} speed={1.5} />
                    </div>
                ) : (
                    <>
                        <div className={classes.statsContainer}>
                            <div className={classes.stat}>
                                <p>Invalid PINs</p>
                                <h1>{getSecurityStats(securityData).totalInvalidPins}</h1>
                            </div>
                            <div className={classes.stat}>
                                <p>Gateway Tampers</p>
                                <h1>{getSecurityStats(securityData).totalTampers}</h1>
                            </div>
                        </div>
                        {getSecurityStats(securityData).totalInvalidPins === 0 && getSecurityStats(securityData).totalTampers === 0 ? (
                            <p style={{ fontSize: "14px", fontWeight: "400", marginTop: "38px", textAlign: "center" }}>
                                No security analytics for the selected time period.
                            </p>
                        ) : (
                            securityData.length <= 1 ? (
                                <div className={`${classes.securityChart} ${isCollapsed ? '' : classes.securityChartCol}`} style={{ marginTop: "8px" }}>
                                    <Bar
                                        data={fallbackLineData1}
                                        options={{
                                        scales: {
                                            y: {
                                                ticks: {
                                                    stepSize: 1,
                                                    precision: 0
                                                }
                                            }
                                        }
                                    }}
                                    />
                                </div>
                            ) : (
                            <div className={`${classes.securityChart} ${isCollapsed ? '' : classes.securityChartCol}`}>
                                <Line 
                                    data={lineData1}
                                    options={{
                                        scales: {
                                            y: {
                                                ticks: {
                                                    stepSize: 1,
                                                    precision: 0
                                                }
                                            }
                                        }
                                    }} 
                                />
                            </div>
                            )
                        )}
                    </>
                )}
                

            </div>
            
        )}

        {tabs[activeTab] === "Padlocks" && (
            <>
            <Dropdown
                value={selectedLock}
                options={lockOptions}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => setSelectedLock(e.value)}
                placeholder="Choose a lock"
                className={classes.dropdown}
            />
            {selectedLock ? (
            <div className={isCollapsed ? classes.padlocksContainer : classes.padlocksContainerCol}>
                <div className={classes.padlocksLeft}>
                        <div className={`${classes.statsHeader} ${isCollapsed ? classes.statsHeader : classes.statsHeaderCol}`}>
                            <div
                                className={classes.title}
                            >
                                <h2>Padlock unlocks</h2>
                                <p>Unlock activity by lock</p>
                            </div>
                            <div className={classes.timeButtons}>
                                {times.map((time, i) => (
                                    <button 
                                    key={time} 
                                    className={`classes.timeButton ${selectedTime === time ? classes.timeButtonActive : classes.timeButton}`}
                                    onClick={() => setSelectedTime(time)}
                                >
                                        {time}
                                    </button>
                                ))}
                            </div>
                        </div>
                            
                        {isLoadingLocks ? (
                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "440px" }}> 
                                <Oval 
                                stroke="var(--Primary-Color)" 
                                height={80} width={40} speed={1.5}
                                />
                            </div>
                        ) : getLockStats(unlockData).totalUnlocks === 0 ? (
                            <p style={{ fontSize: "14px", fontWeight: "400", marginTop: "38px", textAlign: "center" }}>
                                No padlock analytics for the selected time period.
                            </p>
                        ) : (
                        <>
                        <div className={classes.padlockStatsContainer}>
                                <div className={classes.padlockStat}>
                                    <p>Total</p>
                                    <h1>{getLockStats(unlockData).totalUnlocks}</h1>
                                </div>
                        </div>
                        {unlockData.length <= 1 ? (
                            <div className={`${classes.padlockChart} ${isCollapsed ? classes.padlockChartCollapsed : classes.padlockChartCol}`}>
                                <Bar
                                    data={fallbackLineData2}
                                    options={{
                                        scales: {
                                            y: {
                                                ticks: {
                                                    stepSize: 1,
                                                    precision: 0
                                                }
                                            }
                                        }
                                    }}
                                />
                            </div>
                        ) : (
                            <div className={`${classes.padlockChart} ${isCollapsed ? classes.padlockChartCollapsed : classes.padlockChartCol}`}>
                                <Line
                                    data={lineData2}
                                    options={{
                                        scales: {
                                            y: {
                                                ticks: {
                                                    stepSize: 1,
                                                    precision: 0
                                                }
                                            }
                                        }
                                    }} />
                            </div>
                        )}
                    </>
                    )}                
                    </div> 

                <div style ={{ display: "flex", flexDirection: "column"}}>
                    <div className={`${classes.title} ${isCollapsed ? classes.title : classes.titleCol}`}>
                        <h2>Top padlock users</h2>
                        <p>Most active users this {selectedTime.toLowerCase()}</p>
                    </div>

                    {isLoadingLocks ? (
                        <div className={`${classes.topUsersChart} ${isCollapsed ? classes.topUsersChartCollapsed : classes.topUsersChartCol}`} style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <Oval
                            stroke="var(--Primary-Color)"
                            height={80} width={40} speed={1.5}
                            />
                        </div>
                    ) : getLockStats(unlockData).totalUnlocks === 0 ? (
                            <p style={{ fontSize: "14px", fontWeight: "400", marginTop: "38px", textAlign: "center" }}>
                                No data available.
                            </p>
                    ) : (
                        <div className={`${classes.topUsersChart} ${isCollapsed ? classes.topUsersChartCollapsed : classes.topUsersChartCol}`}>
                            <Bar
                                data={barData2}
                                options={{
                                    indexAxis: 'y',
                                    plugins: {
                                        legend: {
                                            display: false
                                        }
                                    }
                                }}
                            />
                        </div>
                    )}

                </div>
            </div>
            ) : (
                <div style={{ fontSize: "14px", fontWeight: "400", marginTop: "38px", textAlign: "center" }}>
                    <p>Please select a lock</p>
                </div>
            )}
            </>
        )}

        </>
    );
}

export default AnalyticsPage;